import { useEffect, useState } from "react";
import { Link2, Share2, Quote, UserPlus, MessageCircle, Smartphone, X } from "lucide-react";
import type { InSnipCard } from "./types";
import { quoteCandidates } from "./quoteLines";
import { shareQuoteImage } from "./shareImage";
import { shareStoryImage } from "./v3/shareV3";
import { cardShareUrl, cardShareUrlSync, prewarmCardShareUrl, withReferral } from "./engagement";

/** This reader's invite link (carries their device id before sign-in, never an account uid). */
function inviteLink(): string {
  const origin =
    typeof window !== "undefined" && /^https:\/\//.test(window.location.origin) && !/localhost/.test(window.location.origin)
      ? window.location.origin.replace(/\/+$/, "")
      : "https://insnip.com";
  return withReferral(`${origin}/`);
}

/**
 * Share surface: one Share action (the card image + its short link, sent together via the
 * native share sheet), then tap a standout line to share it as a quote card. Theme-aligned
 * via CSS variables; inline styles to avoid the shared stylesheet.
 */
export default function ShareSheet({
  card,
  onClose,
  onShareImage,
  onShared,
}: {
  card: InSnipCard;
  onClose: () => void;
  onShareImage: () => void;
  onShared?: () => void;
}): React.ReactElement {
  const lines = quoteCandidates(card);
  const [busy, setBusy] = useState<string | null>(null);
  const [invited, setInvited] = useState(false);
  const [linkDone, setLinkDone] = useState(false);
  // WARMED WHEN THE SHEET OPENS, NOT WHEN SHARE IS PRESSED. /shorten is a real network call
  // (~590ms on prod), and awaiting it inside the Share handler puts an await between the
  // user's tap and navigator.share(), which on iOS Safari can spend the transient activation
  // and throw NotAllowedError. By the time anyone has read the sheet and tapped, the code is
  // already cached, so cardShareUrlSync() below returns the short form without awaiting.
  //
  // The re-render is what makes the warm value visible to the handlers; engagement.ts owns
  // the cache itself, so the card image paths in shareImage.ts share this same mint.
  const [, setWarmed] = useState(0);
  useEffect(() => {
    let alive = true;
    prewarmCardShareUrl(card.id);
    void cardShareUrl(card.id).then(() => {
      if (alive) setWarmed((n) => n + 1);
    });
    return () => {
      alive = false;
    };
  }, [card.id]);

  // A modal dialog must dismiss on Escape; the App's global handler never sees
  // this sheet (it renders above the feed shortcuts), so it owns its own key.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose(); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  // Growth surface: the share sheet doubles as an invite. Shares the referral link natively,
  // else copies it and shows a brief "Link copied" confirmation.
  async function invite(): Promise<void> {
    const url = inviteLink();
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share({ title: "InSnip", text: "Read InSnip with me. The day's best stories in under a minute, free:", url });
        onClose();
      } else if (typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        setInvited(true);
        window.setTimeout(() => setInvited(false), 1800);
      }
    } catch {
      /* share cancelled */
    }
  }

  /** The card's own public page (server-rendered, with the story, the app smart
   *  banner and the store badges waiting for whoever opens it), with referral.
   *
   *  PREFERS THE SHORT CODE. insnip.com/s/QMatDZ is 27 characters; the long form,
   *  insnip.com/c/snip-24b21e1f8a694db1?r=2QD7xo98zmVI1RJ50FBlBZnKSQI2, is 73, and the
   *  half of it that is a 16 character hex id and a 28 character Firebase uid is noise to
   *  every human who sees it. In a WhatsApp message the long one wraps onto its own line
   *  and reads like tracking junk, which is the opposite of what a forwarded story wants
   *  to look like.
   *
   *  The short link resolves to this same page and re-attaches ?r= on the way, so nothing
   *  about attribution or the preview changes. Falls back to the long permalink whenever
   *  the code has not arrived, so sharing is never blocked or broken by a slow mint. */
  function cardLink(): string {
    return cardShareUrlSync(card.id);
  }

  // The plain link: opens the card's page anywhere, which prompts the app on
  // web and phones alike. Native share where available, else copy + confirm.
  async function shareLink(): Promise<void> {
    const url = cardLink();
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share({ title: card.headline, text: "via InSnip, pure signal, no noise:", url });
        onShared?.();
        onClose();
      } else if (typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        setLinkDone(true);
        onShared?.();
        window.setTimeout(() => setLinkDone(false), 1800);
      }
    } catch {
      /* share cancelled */
    }
  }

  // WhatsApp is the dominant share channel for the young Australian audience. Opens a prefilled
  // chat with the card's headline + its public link (carrying referral attribution).
  function whatsapp(): void {
    const url = cardLink();
    const text = `${card.headline}\n\nvia InSnip, pure signal, no noise:\n${url}`;
    if (typeof window !== "undefined") window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
    onShared?.();
    onClose();
  }

  async function quote(line: string): Promise<void> {
    if (busy) return;
    setBusy(line);
    try {
      const result = await shareQuoteImage(card, line);
      if (result !== "failed") {
        onShared?.();
        onClose();
      }
    } finally {
      setBusy(null);
    }
  }

  // v3: the vertical 1080x1920 story card, the native shape for Instagram and TikTok,
  // which are now the top news channels for the young Australian audience.
  async function story(): Promise<void> {
    if (busy) return;
    setBusy("__story__");
    try {
      const result = await shareStoryImage(card);
      if (result !== "failed") {
        onShared?.();
        onClose();
      }
    } finally {
      setBusy(null);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Share"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 95, background: "rgba(0,0,0,0.55)",
        backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 540, maxHeight: "84vh", overflowY: "auto",
          background:
            "linear-gradient(180deg, color-mix(in srgb, var(--surface-raised, var(--bg-soft)) 90%, transparent) 0%, color-mix(in srgb, var(--bg) 97%, transparent) 100%)",
          backdropFilter: "blur(34px) saturate(1.25)", WebkitBackdropFilter: "blur(34px) saturate(1.25)",
          border: "1px solid color-mix(in srgb, var(--accent) 24%, var(--line))", borderBottom: "none",
          borderRadius: "30px 30px 0 0", boxShadow: "0 -30px 90px rgba(0,0,0,0.5)",
          padding: "14px 18px calc(22px + env(safe-area-inset-bottom))",
          color: "var(--ink)", fontFamily: "Inter, system-ui, sans-serif",
        }}
      >
        <div style={{ width: 40, height: 4, borderRadius: 4, background: "color-mix(in srgb, var(--ink) 22%, transparent)", margin: "0 auto 14px" }} />

        {/* What you are sharing: the story itself, presented like a keepsake. */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          {card.imageUrl ? (
            <img
              src={card.imageUrl}
              alt=""
              aria-hidden="true"
              style={{
                width: 52, height: 52, objectFit: "cover", borderRadius: 14, flex: "none",
                boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.12), 0 8px 22px -10px rgba(0,0,0,0.5)",
              }}
            />
          ) : null}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{
              fontSize: 9.5, fontWeight: 800, letterSpacing: "0.26em", textTransform: "uppercase",
              color: "color-mix(in srgb, var(--accent) 75%, var(--muted))", marginBottom: 3,
            }}>
              Share this snippet
            </div>
            <div style={{
              font: '600 14px/1.35 var(--font-serif, "Source Serif 4", Georgia, serif)',
              display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
            }}>
              {card.headline}
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={{ background: "transparent", border: "none", color: "var(--muted)", cursor: "pointer", padding: 4, lineHeight: 0, flex: "none" }}>
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        {/* One Share action: the card image + its short link, together. Gilded, primary. */}
        <button
          type="button"
          onClick={() => { onShareImage(); onClose(); }}
          style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%",
            cursor: "pointer", padding: "14px 16px", borderRadius: 999,
            background:
              "linear-gradient(150deg, color-mix(in srgb, var(--accent) 24%, transparent) 0%, color-mix(in srgb, var(--accent) 8%, transparent) 75%)",
            border: "1px solid color-mix(in srgb, var(--accent) 46%, var(--line))",
            boxShadow: "0 0 26px -8px color-mix(in srgb, var(--accent) 45%, transparent), inset 0 1px 0 rgba(255,255,255,0.1)",
            color: "var(--ink)", font: '700 14.5px Inter, system-ui, sans-serif',
          }}
        >
          <Share2 size={18} color="var(--accent)" aria-hidden="true" />
          <span>Share the story card</span>
        </button>

        {/* v3: the vertical story card for Instagram and TikTok. */}
        <button
          type="button"
          disabled={!!busy}
          onClick={() => void story()}
          style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%",
            cursor: busy ? "default" : "pointer", marginTop: 10, padding: "12px 16px", borderRadius: 14,
            background: "color-mix(in srgb, var(--accent) 8%, transparent)",
            border: "1px solid color-mix(in srgb, var(--accent) 30%, var(--line))",
            color: "var(--ink)", font: '700 14px Inter, system-ui, sans-serif',
            opacity: busy && busy !== "__story__" ? 0.5 : 1,
          }}
        >
          <Smartphone size={17} color="var(--accent)" aria-hidden="true" />
          <span>{busy === "__story__" ? "Creating…" : "Story format, for Instagram and TikTok"}</span>
        </button>

        {/* The plain link: the card's own page, which offers the app wherever it opens. */}
        <button
          type="button"
          onClick={() => void shareLink()}
          style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%",
            cursor: "pointer", marginTop: 10, padding: "12px 16px", borderRadius: 14,
            background: "color-mix(in srgb, var(--accent) 8%, transparent)",
            border: "1px solid color-mix(in srgb, var(--accent) 30%, var(--line))",
            color: "var(--ink)", font: '700 14px Inter, system-ui, sans-serif',
          }}
        >
          <Link2 size={17} color="var(--accent)" aria-hidden="true" />
          <span>{linkDone ? "Link copied" : "Share the link"}</span>
        </button>

        {/* WhatsApp: the channel this audience actually shares news in. */}
        <button
          type="button"
          onClick={whatsapp}
          style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%",
            cursor: "pointer", marginTop: 10, padding: "12px 16px", borderRadius: 14,
            background: "color-mix(in srgb, #25D366 12%, transparent)",
            border: "1px solid color-mix(in srgb, #25D366 34%, var(--line))",
            color: "var(--ink)", font: '700 14px Inter, system-ui, sans-serif',
          }}
        >
          <MessageCircle size={18} color="#25D366" aria-hidden="true" />
          <span>Share on WhatsApp</span>
        </button>

        {/* Subtle growth row: invite a friend (earns a streak freeze), shares the referral link. */}
        <button
          type="button"
          onClick={() => void invite()}
          style={{
            display: "flex", alignItems: "center", gap: 8, width: "100%",
            cursor: "pointer", marginTop: 10, padding: "12px 16px", borderRadius: 14,
            background: "color-mix(in srgb, var(--ink) 4%, transparent)",
            border: "1px solid var(--line)",
            color: "var(--ink)", font: '600 13.5px Inter, system-ui, sans-serif',
          }}
        >
          <UserPlus size={17} color="var(--accent)" aria-hidden="true" />
          <span>{invited ? "Link copied" : "Invite friends"}</span>
          <span style={{ marginLeft: "auto", fontSize: 11.5, fontWeight: 500, color: "var(--muted)" }}>
            {invited ? "" : "Earn a streak freeze"}
          </span>
        </button>

        {lines.length ? (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "20px 2px 3px", fontSize: 14.5, fontWeight: 700, color: "var(--ink)" }}>
              <Quote size={15} color="var(--accent)" aria-hidden="true" /> Share a quote
            </div>
            <p style={{ margin: "0 2px 12px", fontSize: 12.5, lineHeight: 1.45, color: "var(--muted)" }}>
              Tap a line to turn it into a shareable card.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {lines.map((line) => {
                const isBusy = busy === line;
                return (
                  <button
                    key={line}
                    type="button"
                    disabled={!!busy}
                    onClick={() => void quote(line)}
                    style={{
                      textAlign: "left", width: "100%", cursor: busy ? "default" : "pointer",
                      background: isBusy
                        ? "color-mix(in srgb, var(--accent) 14%, transparent)"
                        : "color-mix(in srgb, var(--ink) 4%, transparent)",
                      border: "1px solid var(--line)", borderLeft: "3px solid var(--accent)",
                      borderRadius: 12, padding: "13px 15px", color: "var(--ink)",
                      font: '400 15px/1.5 var(--font-serif, "Source Serif 4", Georgia, serif)',
                      opacity: busy && !isBusy ? 0.5 : 1,
                      transition: "background 0.15s ease, opacity 0.15s ease",
                    }}
                  >
                    {isBusy ? "Creating…" : line}
                  </button>
                );
              })}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
